/**
 * Inbox sidebar (prototype `isInbox` sidebar block, lines 342–367). Bespoke
 * markup — the per-board icon colors and the "Inbox" title row do not map
 * cleanly onto `SidebarRow`, so this is ported 1:1 with inline styles.
 */
"use client";

import { ld } from "@/lib/theme/light-dark";
import {
  AlertTriangle,
  FolderPlus,
  Gauge,
  Inbox,
  KeyRound,
  Receipt,
  Search,
  SquarePen,
  User,
  UserPlus,
  UserX,
  Users,
  Wrench,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import type { InboxBoardId, InboxSidebarSelection } from "./module-inbox-data";
import {
  countInboxBoardConversations,
  inboxAllCount,
  inboxMineCount,
  inboxUnassignedCount,
} from "./module-inbox-data";

const MONO_EYEBROW = {
  fontFamily: H.label,
  fontSize: 10,
  letterSpacing: "0.12em",
  color: ld("rgba(11,15,25,0.34)", "rgba(250,250,250,0.34)"),
  textTransform: "uppercase" as const,
};

/** Single-line labels — long board names must not wrap and push rows below the clip. */
const SIDEBAR_LABEL = {
  flex: 1,
  minWidth: 0,
  lineHeight: 1.25,
  whiteSpace: "nowrap" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
};

type SidebarNavRowProps = {
  readonly icon: ReactNode;
  readonly iconColor?: string;
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
};

function SidebarNavRow(props: SidebarNavRowProps): ReactNode {
  const iconColor: string =
    props.active && props.iconColor !== undefined
      ? H.accent
      : props.iconColor ?? ld("rgba(11,15,25,0.78)", "rgba(250,250,250,0.78)");

  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        appearance: "none",
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "7px 9px",
        borderRadius: 8,
        border: "none",
        fontFamily: H.sans,
        fontSize: 13.5,
        fontWeight: props.active ? 600 : 400,
        color: props.active ? H.accent : ld("rgba(11,15,25,0.78)", "rgba(250,250,250,0.78)"),
        background: props.active ? H.accentSoft : "transparent",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ color: iconColor, display: "inline-flex", flexShrink: 0 }}>
        {props.icon}
      </span>
      <span style={SIDEBAR_LABEL}>{props.label}</span>
      <span
        style={{
          fontSize: 12,
          color: props.active ? H.accent : ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
        }}
      >
        {props.count}
      </span>
    </button>
  );
}

function isBoardActive(
  selection: InboxSidebarSelection,
  boardId: InboxBoardId,
): boolean {
  return selection.kind === "board" && selection.boardId === boardId;
}

export function ModuleInboxSidebar(props: {
  readonly selection: InboxSidebarSelection;
  readonly onSelectionChange: (selection: InboxSidebarSelection) => void;
}): ReactNode {
  const tModuleInboxData = useTranslations("marketing.moduleInboxData");
  const t = useTranslations("marketing.moduleInboxSidebar");
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 6px 12px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("inbox")}</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
            cursor: "default",
          }}
        >
          <SquarePen width={16} height={16} strokeWidth={1.7} />
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 34,
          padding: "0 11px",
          background: ld("#F1F2F4", "#2b2e32"),
          borderRadius: 9,
          color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
          fontSize: 13,
          marginBottom: 12,
        }}
      >
        <Search width={15} height={15} strokeWidth={1.8} />
        {t("suchen")}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SidebarNavRow
          icon={<User width={16} height={16} strokeWidth={1.8} />}
          label={t("meineKonversationen")}
          count={inboxMineCount(tModuleInboxData)}
          active={props.selection.kind === "mine"}
          onClick={() => {
            props.onSelectionChange({ kind: "mine" });
          }}
        />
        <SidebarNavRow
          icon={<Users width={16} height={16} strokeWidth={1.9} />}
          label={t("alleKonversationen")}
          count={inboxAllCount(tModuleInboxData)}
          active={props.selection.kind === "all"}
          onClick={() => {
            props.onSelectionChange({ kind: "all" });
          }}
        />
        <SidebarNavRow
          icon={<UserX width={16} height={16} strokeWidth={1.8} />}
          label={t("unzugewiesen")}
          count={inboxUnassignedCount(tModuleInboxData)}
          active={props.selection.kind === "unassigned"}
          onClick={() => {
            props.onSelectionChange({ kind: "unassigned" });
          }}
        />
      </div>

      <div style={{ ...MONO_EYEBROW, padding: "14px 9px 6px" }}>
        {t("themen")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SidebarNavRow
          icon={<Inbox width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#6B7280", "#a6aaaf")}
          label={t("allgemein")}
          count={countInboxBoardConversations(tModuleInboxData, "allgemein")}
          active={isBoardActive(props.selection, "allgemein")}
          onClick={() => {
            props.onSelectionChange({ kind: "board", boardId: "allgemein" });
          }}
        />
        <SidebarNavRow
          icon={<AlertTriangle width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#B65C5C", "#eb8c8a")}
          label={t("schaeden")}
          count={countInboxBoardConversations(tModuleInboxData, "schaeden")}
          active={isBoardActive(props.selection, "schaeden")}
          onClick={() => {
            props.onSelectionChange({ kind: "board", boardId: "schaeden" });
          }}
        />
        <SidebarNavRow
          icon={<KeyRound width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#A8824B", "#cba36c")}
          label={t("schluesselbestellungen")}
          count={countInboxBoardConversations(
            tModuleInboxData,
            "schluesselbestellungen",
          )}
          active={isBoardActive(props.selection, "schluesselbestellungen")}
          onClick={() => {
            props.onSelectionChange({
              kind: "board",
              boardId: "schluesselbestellungen",
            });
          }}
        />
        <SidebarNavRow
          icon={<UserPlus width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#4F77AE", "#83ade8")}
          label={t("mietinteressenten")}
          count={countInboxBoardConversations(
            tModuleInboxData,
            "mietinteressenten",
          )}
          active={isBoardActive(props.selection, "mietinteressenten")}
          onClick={() => {
            props.onSelectionChange({
              kind: "board",
              boardId: "mietinteressenten",
            });
          }}
        />
        <SidebarNavRow
          icon={<Receipt width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#4C8C6B", "#7bbc98")}
          label={t("rechnungen")}
          count={countInboxBoardConversations(tModuleInboxData, "rechnungen")}
          active={isBoardActive(props.selection, "rechnungen")}
          onClick={() => {
            props.onSelectionChange({ kind: "board", boardId: "rechnungen" });
          }}
        />
        <SidebarNavRow
          icon={<Wrench width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#B0703E", "#dd9a67")}
          label={t("wartungen")}
          count={countInboxBoardConversations(tModuleInboxData, "wartungen")}
          active={isBoardActive(props.selection, "wartungen")}
          onClick={() => {
            props.onSelectionChange({ kind: "board", boardId: "wartungen" });
          }}
        />
        <SidebarNavRow
          icon={<Gauge width={16} height={16} strokeWidth={1.8} />}
          iconColor={ld("#6B7280", "#a6aaaf")}
          label={t("zaehlerstaende")}
          count={countInboxBoardConversations(
            tModuleInboxData,
            "zaehlerstaende",
          )}
          active={isBoardActive(props.selection, "zaehlerstaende")}
          onClick={() => {
            props.onSelectionChange({
              kind: "board",
              boardId: "zaehlerstaende",
            });
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "12px 9px 0",
          fontSize: 13,
          color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
          cursor: "default",
        }}
      >
        <FolderPlus width={15} height={15} strokeWidth={1.8} />
        {t("neuesInboxThema")}
      </div>
    </div>
  );
}
